import React from 'react';
import { motion } from 'framer-motion';

const C = {
  cream: '#F5F1E8',
  gold: '#C6A96B',
  darkText: '#2C2C2C',
  darkTextSec: '#5A5A5A',
};

const fadeUp = {
  initial: { y: 20, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const Section = ({ title, children }) => (
  <div className="mb-12">
    <h2 className="text-lg font-light mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkText }}>{title}</h2>
    <div className="space-y-3 text-sm leading-relaxed" style={{ color: C.darkTextSec }}>{children}</div>
  </div>
);

const BulletList = ({ items }) => (
  <div className="space-y-2">
    {items.map((item, i) => (
      <div key={i} className="flex items-start gap-2.5">
        <div className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: `${C.gold}55` }} />
        <p className="text-sm leading-relaxed" style={{ color: C.darkTextSec }}>{item}</p>
      </div>
    ))}
  </div>
);

const LpTermsPage = () => (
  <div style={{ fontFamily: "'Manrope', sans-serif", background: C.cream, color: C.darkText, minHeight: '100vh' }}>
    <div className="max-w-2xl mx-auto px-6 md:px-12 py-20 md:py-28">
      <motion.div {...fadeUp}>
        <img
          src="/sent2ment-logo.png"
          alt="Sent2ment"
          className="h-16 w-auto object-contain mb-10"
        />

        <h1 className="text-3xl sm:text-4xl font-light tracking-tight mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Terms & Conditions</h1>
        <p className="text-sm italic mb-12" style={{ color: C.darkTextSec, fontFamily: "'Cormorant Garamond', serif" }}>Please read these terms carefully before placing an order.</p>

        <Section title="About Sent2ment">
          <p>Sent2ment Ltd provides premium keepsake products that allow you to preserve personal messages, voice recordings, photos, and videos — accessible via a unique QR code linked to your product.</p>
        </Section>

        <Section title="Our products">
          <p>Sent2ment offers two complementary types of product:</p>
          <BulletList items={[
            'Physical keepsakes — letters, cards, journals and pocket-sized items, posted to you or your chosen recipient.',
            'Digital experiences — including the Memory Vault, recipient viewing pages, voice notes, affirmation cards and journal pages, accessed via a unique QR code or link.',
          ]} />
          <p>Some products combine both — for example, a physical Pocket Keepsake Card with a QR code that opens its digital counterpart in the Memory Vault. The digital experience is part of the product you purchase and is governed by these terms.</p>
        </Section>

        <Section title="Placing an order">
          <p>By placing an order, you agree to these Terms & Conditions. All orders are subject to availability. We reserve the right to refuse or cancel any order at our discretion.</p>
          <p>Prices are displayed in GBP (£) and include VAT where applicable. Payment is processed securely via Stripe at the point of checkout.</p>
        </Section>

        <Section title="Delivery">
          <p>Our delivery times are 7&ndash;10 days, however in busy periods it may take a little longer as every keepsake is made to order with precision and care. Delivery times may vary depending on your location. UK delivery is free of charge.</p>
          <p>Once dispatched, we will notify you by email. We are not responsible for delays caused by couriers or circumstances outside our control.</p>
        </Section>

        <Section title="Your content">
          <p>After purchase, you may upload personal content — including voice messages, videos, images, and written text — to your private QR-linked memory space.</p>
          <p>You are solely responsible for the content you upload. By uploading content, you confirm that:</p>
          <BulletList items={[
            'You have the legal right and permission to share that content',
            'The content does not infringe any third-party rights',
            'The content does not contain unlawful, harmful, or offensive material',
          ]} />
          <p>We reserve the right to remove content that violates these terms.</p>
        </Section>

        <Section title="Digital content & storage">
          <p>By purchasing a Sent2ment product, you acknowledge and consent to the secure storage and processing of any digital content you provide (including messages, images, audio, or video) for the duration of your selected storage plan, and its linkage to your unique QR code.</p>
          <p>You confirm that you have the right to upload and share any content provided, including where it relates to another individual.</p>
        </Section>

        <Section title="Storage">
          <p>Standard storage is provided for 10 years from the date of upload. An optional upgrade to 25 years is available at the time of purchase.</p>
          <p>After the storage period expires, your content may be permanently deleted. We may attempt to notify you before deletion, but this is not guaranteed.</p>
          <p>The customer is responsible for keeping their own backups of any content they wish to retain beyond the storage period. If you would like a personal copy of your uploaded content, please contact <a href="mailto:hello@sent2ment.co.uk" className="transition-colors duration-300 hover:text-[#C6A96B]" style={{ color: C.gold }}>hello@sent2ment.co.uk</a> and we&rsquo;ll be glad to help.</p>
        </Section>

        <Section title="Right to request deletion">
          <p>You may request deletion of your uploaded content at any time, for any reason, by contacting <a href="mailto:hello@sent2ment.co.uk" className="transition-colors duration-300 hover:text-[#C6A96B]" style={{ color: C.gold }}>hello@sent2ment.co.uk</a>.</p>
          <p>Once a deletion request is processed, the content is removed from our active storage and any associated digital experiences (e.g. recipient view) will no longer display the content.</p>
        </Section>

        <Section title="Returns & refunds">
          <p>Due to the personalised nature of our products, orders cannot be cancelled, returned, or refunded once personalisation or production has begun.</p>
          <p>Personalised digital products — including the Sent2ment Memory Vault, whether purchased standalone or as an add-on — are non-refundable once they have been created or accessed. By completing your purchase, you acknowledge that you are receiving a bespoke digital service and waive your right to cancel under the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013 once provision of that service has begun.</p>
          <p>If your product arrives damaged or faulty, please contact us within 14 days of delivery. Where appropriate, we will offer a replacement or remedy in accordance with your statutory rights.</p>
          <p>Nothing in these terms affects your legal rights under UK consumer law.</p>
        </Section>

        <Section title="Intellectual property">
          <p>All content, branding, design, and materials provided by Sent2ment Ltd remain the intellectual property of Sent2ment Ltd and may not be copied, reproduced, or distributed without permission.</p>
          <p>You retain ownership of the personal content you upload.</p>
        </Section>

        <Section title="Service availability">
          <p>We aim to provide continuous access to your stored content; however, we do not guarantee uninterrupted or error-free access.</p>
          <p>Access may be affected by maintenance, technical issues, or circumstances beyond our control.</p>
        </Section>

        <Section title="Limitation of liability">
          <p>To the fullest extent permitted by law, Sent2ment Ltd shall not be liable for:</p>
          <BulletList items={[
            'Loss of data, content, or memories',
            'Loss of profits, revenue, or opportunity',
            'Any indirect or consequential loss',
          ]} />
          <p>Nothing in these terms limits or excludes liability where it is unlawful to do so.</p>
        </Section>

        <Section title="Changes to these terms">
          <p>We may update these Terms & Conditions from time to time. The latest version will always be available on our website.</p>
        </Section>

        <Section title="Contact">
          <p>For any questions about these terms, please contact:</p>
          <p><a href="mailto:hello@sent2ment.co.uk" className="transition-colors duration-300 hover:text-[#C6A96B]" style={{ color: C.gold }}>hello@sent2ment.co.uk</a></p>
        </Section>

        <div className="border-t pt-8 mt-16 text-center" style={{ borderColor: `${C.gold}15` }}>
          <p className="text-xs italic mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold }}>Sent2ment Ltd — protecting what matters most.</p>
          <p className="text-[11px] leading-relaxed mb-2" style={{ color: `${C.darkTextSec}AA` }}>
            Sent2ment<sup style={{ fontSize: '0.7em', verticalAlign: 'super' }}>®</sup> is a registered trademark in the United Kingdom.
          </p>
          <p className="text-[11px] leading-relaxed" style={{ color: `${C.darkTextSec}AA` }}>
            All content, product concepts, and digital memory systems are the intellectual property of Sent2ment Ltd.
          </p>
        </div>
      </motion.div>
    </div>
  </div>
);

export default LpTermsPage;
